import config from '../config';
import { navigateToTenant, getSubdomain } from '../utils/tenantUtils';

export interface RequestOptions {
  headers?: Record<string, string>;
  params?: Record<string, any>;
  onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void;
  responseType?: XMLHttpRequestResponseType;
}

class HttpClient {
  private baseUrl = config.API_URL;

  async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request(endpoint, 'GET', undefined, options);
  }

  async post<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request(endpoint, 'POST', data, options);
  }

  async put<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request(endpoint, 'PUT', data, options);
  }

  async patch<T>(endpoint: string, data?: any, options?: RequestOptions): Promise<T> {
    return this.request(endpoint, 'PATCH', data, options);
  }

  async delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request(endpoint, 'DELETE', undefined, options);
  }

  private async request<T>(
    endpoint: string,
    method: string,
    data?: any,
    options?: RequestOptions
  ): Promise<T> {
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = {
      ...(!(data instanceof FormData) && { 'Content-Type': 'application/json' }),
      ...options?.headers
    };

    // Don't clobber an explicitly-provided Authorization header (e.g. the 2FA
    // pre-auth token used by /verify-otp and /resend-otp).
    if (token && !headers['Authorization']) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    if (!headers['X-Tenant-ID']) {
      // if the request explicitly sets a header, consider it an override.
      const storedTenantId = localStorage.getItem('tenantId');
      if (storedTenantId) {
        headers['X-Tenant-ID'] = storedTenantId;
      } else {
        console.warn('HTTP Client - No tenantId found in localStorage');
      }
    }

    // For FormData uploads, log the total size being sent
    if (data instanceof FormData) {
      let totalSize = 0;
      for (const pair of data.entries()) {
        if (pair[1] instanceof File) {
          totalSize += pair[1].size;
        }
      }
    }

    try {
      const xhr = new XMLHttpRequest();

      const formattedEndpoint = endpoint.replace(/^\//, '');

      let url = `${this.baseUrl}/${formattedEndpoint}`;

      if (options?.params) {
        const queryParams = new URLSearchParams();
        Object.entries(options.params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              value.forEach((item) => {
                if (item !== undefined && item !== null) {
                  queryParams.append(key, item.toString());
                }
              });
            } else {
              queryParams.append(key, value.toString());
            }
          }
        });
        const queryString = queryParams.toString();
        if (queryString) {
          url += `?${queryString}`;
        }
      }

      return new Promise((resolve, reject) => {
        xhr.open(method, url);

        xhr.withCredentials = true;

        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

        if (options?.responseType) {
          xhr.responseType = options.responseType;
        }

        Object.entries(headers).forEach(([key, value]) => {
          if (value !== undefined) {
            xhr.setRequestHeader(key, value);
          }
        });

        if (options?.onUploadProgress) {
          xhr.upload.onprogress = options.onUploadProgress;
        }

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            if (xhr.responseType === 'arraybuffer') {
              resolve(xhr.response as T);
            } else {
              try {
                const response = xhr.responseText ? JSON.parse(xhr.responseText) : null;
                resolve(response);
              } catch {
                resolve(xhr.responseText as T);
              }
            }
          } else {
            try {
              // Handle 401 Unauthorized by redirecting to login page
              if (xhr.status === 401) {
                // Clear authentication data
                localStorage.removeItem('token');
                localStorage.removeItem('user');

                // Keep the tenant ID for login page
                const tenantId = localStorage.getItem('tenantId');

                // Check if we're already on the login page to prevent infinite loops
                const isLoginPage = window.location.pathname.includes('/login');
                if (isLoginPage) {
                  // If already on login page, don't redirect again, just reject the request
                  reject(new Error('Authentication failed'));
                  return;
                }

                // Redirect to login page
                const currentSubdomain = getSubdomain();
                if (tenantId) {
                  // If we have a tenant ID and we're not on a subdomain, navigate to tenant subdomain
                  if (!currentSubdomain) {
                    navigateToTenant(tenantId, '/login');
                  } else {
                    // Already on subdomain, just go to login page
                    window.location.href = '/login';
                  }
                } else {
                  // Otherwise redirect to main login
                  window.location.href = '/login';
                }

                reject(new Error('Authentication failed. Redirecting to login page...'));
              } else {
                // Handle other errors normally
                const errorData = xhr.responseText
                  ? JSON.parse(xhr.responseText)
                  : { message: 'Request failed' };
                reject(new Error(errorData.message));
              }
            } catch {
              reject(new Error(`HTTP error! status: ${xhr.status}`));
            }
          }
        };

        xhr.onerror = (event) => {
          console.error('XHR Error details:', event);
          // Check if this is a CORS error
          if (xhr.status === 0) {
            reject(new Error('Network error: CORS issue or server unreachable'));
          } else {
            reject(new Error(`Network error: ${xhr.statusText || 'Unknown error'}`));
          }
        };

        // Add timeout handler
        xhr.ontimeout = () => {
          reject(new Error('Request timed out. The server took too long to respond.'));
        };

        xhr.withCredentials = true;

        if (data instanceof FormData) {
          xhr.send(data);
        } else if (data) {
          xhr.send(JSON.stringify(data));
        } else {
          xhr.send();
        }
      });
    } catch (error) {
      console.error('Request error:', error);
      throw error instanceof Error ? error : new Error('An unexpected error occurred');
    }
  }
}

export const httpClient = new HttpClient();
